import { useCallback, useState, type ReactNode } from 'react';
import { PIN_LENGTH, checkPin } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { PinPad } from './PinPad.js';

/**
 * La richiesta del PIN prima di riprodurre (SPEC §3.13).
 *
 * ## Perché **prima di riprodurre** e non prima di aprire la scheda
 *
 * Perché una scheda non mostra niente che vada protetto: titolo, locandina, trama. Chiedere il PIN
 * per leggerla trasformerebbe il controllo genitori in un ostacolo per chi sfoglia, e chi lo sente
 * come un ostacolo lo spegne. Si chiede dove comincia la cosa da proteggere.
 *
 * ## Il PIN sbagliato non porta via
 *
 * Si svuotano i pallini, compare «PIN errato. Riprova.», e **l'azione resta in attesa**: quando il
 * PIN è giusto la riproduzione parte da sé, non va rifatta. Riportare alla scheda vorrebbe dire
 * ricominciare da capo per un errore di battitura, che è la ragione per cui la gente smette di usare
 * un controllo genitori.
 *
 * ## Senza un PIN archiviato questa finestra non si apre
 *
 * È il presidio contro il caso peggiore possibile: contenuto bloccato e nessuna chiave. Lo decide
 * chi sta sopra — `isLocked` vuole `pinEnabled` — e qui `storedPin` non nullo è la stessa condizione
 * scritta due volte apposta.
 */

export interface PinPromptProps {
  readonly focusPort: FocusPort;
  /** Il titolo che si sta per guardare: la frase lo nomina, altrimenti non si sa cosa si sblocca. */
  readonly title: string;
  readonly storedPin: string;
  readonly onUnlocked: () => void;
  readonly onCancel: () => void;
}

export function PinPrompt({ focusPort, title, storedPin, onUnlocked, onCancel }: PinPromptProps): ReactNode {
  const translator = useTranslator();
  const [entered, setEntered] = useState('');
  const [wrong, setWrong] = useState(false);

  const digit = useCallback(
    (value: string) => {
      setWrong(false);
      setEntered((current) => {
        if (current.length >= PIN_LENGTH) return current;
        const next = current + value;
        // Alla quarta cifra si verifica da sé: un OK in più dopo l'ultima non aggiunge niente, e su
        // un telecomando ogni pressione si sente.
        const verdict = checkPin(next, storedPin);
        if (verdict === 'accepted') {
          onUnlocked();
          return '';
        }
        if (verdict === 'rejected') {
          setWrong(true);
          return '';
        }
        return next;
      });
    },
    [storedPin, onUnlocked],
  );

  return (
    <div
      data-modal="1"
      data-pin-prompt="1"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 80,
        background: 'var(--scrim, rgba(5,7,10,.92))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 26,
        padding: 40,
      }}
    >
      <span style={{ font: `700 22px ${FONT_STACK}`, color: colors.accent, letterSpacing: '.1em', textTransform: 'uppercase' }}>
        {translator.t('pin.prompt.title')}
      </span>
      <span style={{ font: `600 32px ${FONT_STACK}`, color: colors.text, textAlign: 'center', maxWidth: 900 }}>
        {translator.t('pin.prompt.body', { title })}
      </span>

      <PinPad
        focusPort={focusPort}
        entered={entered.length}
        length={PIN_LENGTH}
        error={wrong ? translator.t('pin.error.wrong') : null}
        onDigit={digit}
        onErase={() => setEntered((current) => current.slice(0, -1))}
        onCancel={onCancel}
      />
    </div>
  );
}
